import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Video, FlaskConical, Wrench, BookOpen, Book, Clock, ArrowRight } from 'lucide-react';
import { getContentThumbnail } from '@/utils/thumbnailUtils';

/**
 * Content Card Component
 * Displays a content item (video, simulation, tool, course, book) in a card format
 * 
 * Features:
 * - Thumbnail image
 * - Title
 * - Content type badge (Video/Simulation/Tool/Course/Book)
 * - Duration/Read time metadata
 * - Action button: "Watch Now" / "Open Simulation" / "Try Tool" / "Start Course" / "Read Now"
 */
export default function ContentCard({ content, onClick }) {
  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'simulation':
        return <FlaskConical className="h-5 w-5" />;
      case 'tool':
        return <Wrench className="h-5 w-5" />;
      case 'course':
        return <BookOpen className="h-5 w-5" />;
      case 'book':
        return <Book className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getActionButtonText = (contentType) => {
    switch (contentType) {
      case 'video':
        return 'Watch Now';
      case 'simulation':
        return 'Open Simulation';
      case 'tool':
        return 'Try Tool';
      case 'course':
        return 'Start Course';
      case 'book':
        return 'Read Now';
      default:
        return 'Open';
    }
  };

  const getDurationText = (content) => {
    if (content.content_type === 'video' && content.video_duration_formatted) {
      return content.video_duration_formatted;
    }
    if (content.content_type === 'course' && content.course_duration_hours) {
      return `${content.course_duration_hours} hours`;
    }
    if (content.content_type === 'book' && content.read_time_minutes) {
      return `${content.read_time_minutes} min read`;
    }
    return null;
  };

  const thumbnailUrl = getContentThumbnail(content, 400, 300);

  return (
    <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full flex flex-col">
      <CardContent className="p-0 flex flex-col h-full">
        {/* Thumbnail */}
        <div className="w-full h-40 overflow-hidden rounded-t-lg">
          <img
            src={thumbnailUrl}
            alt={content.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-start gap-2 mb-2">
            {getContentIcon(content.content_type)}
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1 line-clamp-2">{content.title}</h3>
              {content.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                  {content.description}
                </p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between mt-auto">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{content.content_type}</Badge>
              {getDurationText(content) && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>{getDurationText(content)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Button */}
          <Button
            className="w-full mt-3"
            onClick={(e) => {
              e.stopPropagation();
              onClick && onClick();
            }}
          >
            {getActionButtonText(content.content_type)}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

